import {
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	TextField,
	Typography
} from "@mui/material";
import React, {
	ChangeEvent,
	FC,
	forwardRef,
	ReactNode,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NumbersIcon from "@mui/icons-material/Numbers";
import SquareIcon from "@mui/icons-material/Square";
import DescriptionIcon from "@mui/icons-material/Description";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import RememberMeIcon from "@mui/icons-material/RememberMe";
import AddCommentIcon from "@mui/icons-material/AddComment";
import SaveIcon from "@mui/icons-material/Save";
import LockIcon from "@mui/icons-material/Lock";

import "./eventLogs.css";
import {
	ICoordinate,
	IDimensions,
	IEventLogDetails,
	IEventLogRowDetails,
	IGetTurnaroundLogsRequestData,
	ISocietyConfigDetails
} from "../../types";
import { ReactComponent as NoImageIcon } from "../../assets/icons/image-not-found-icon.svg";
import moment from "moment";
import { LoadingButton } from "@mui/lab";
import { convertToTitleCase, downloadImageFromUrl, getFormattedDuration } from "../../utils/commonUtils";
import { PAGINATION_CONSTANTS, USER_ROLES_E, VEHICLE_TYPE_E } from "../../utils/constants";
// import { ReactComponent as BikeIcon } from "../../assets/icons/bike-icon.svg";
// import { ReactComponent as CarIcon } from "../../assets/icons/car-icon.svg";
import { ReactComponent as BikeEntryIcon } from "../../assets/icons/dash-bike-entry.svg";
import { ReactComponent as BikeExitIcon } from "../../assets/icons/dash-bike-exit.svg";
import { ReactComponent as CarEntryIcon } from "../../assets/icons/dash-car-entry.svg";
import { ReactComponent as CarExitIcon } from "../../assets/icons/dash-car-exit.svg";
import { useGetTurnaroundLogsQuery } from "../../redux/reducers/dashboard.reducer";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import VehicleTypeIcon from "../../components/VehicleTypeIcon";
import { useEditEventLogMutation } from "../../redux/reducers/eventLogs.reducer";
import ArchivedImage from "../../assets/images/archived.png";
import { useAppSelector } from "../../redux";
import { useGetSocietyConfigQuery, useGetSystemIntegratorDetailsQuery } from "../../redux/reducers/settings.reducer";

interface ILogDetailsDialogProps {
	open: boolean;
	eventLogDetails: IEventLogRowDetails | null;
	previousButtonDisabled?: boolean;
	nextButtonDisabled?: boolean;
	pageChangeLoading?: boolean;
	tatEnabled?: boolean;
	onClose: () => void;
	onPrevious: (eventId: string) => void;
	onNext: (eventId: string) => void;
	onEditEventSuccess: (eventLogDetails: IEventLogDetails) => void;
}

enum LOG_DETAILS_TAB_E {
	IMAGE,
	HISTORY
}

type LogDetailsTabRendererType = {
	[key in LOG_DETAILS_TAB_E]: ReactNode;
};

interface ITurnaroundLogsRowDetails {
	id: number;
	entry_timestamp: string;
	exit_timestamp: string;
	time_spent: string;
}

interface IImageOverlayDetails {
	productName: string;
	timestamp: string;
	vehicleType: string;
	numberPlate: string;
	clientName: string;
	auth: string;
	location: string;
}

interface IFrameImageProps {
	src: string;
	bbox: [number, number, number, number][];
	overlayDetails: IImageOverlayDetails;
	width: number;
	frameID: string;
	loading?: boolean;
	archived?: boolean;
	setShowDownloadButtonLoader: React.Dispatch<React.SetStateAction<boolean>>;
	openFullscreen?: () => void;
}

interface IFrameImageRef {
	downloadImage: () => void;
}

const BBOX_STROKE_COLOR = "#05ff05";
const BBOX_STROKE_WIDTH = 1.25;

// Minimum swipe distance (in px)
const minSwipeDistance = 50;

const FrameImage = forwardRef<IFrameImageRef, IFrameImageProps>((props, ref) => {
	const { src, bbox, overlayDetails, width, frameID, loading, archived, setShowDownloadButtonLoader, openFullscreen } =
		props;

	const canvasRef = useRef<HTMLCanvasElement>(null);
	const downloadCanvasRef = useRef<HTMLCanvasElement>(null);

	const [showError, setShowError] = useState<boolean>(false);
	const [canvasDimensions, setCanvasDimensions] = useState<IDimensions>({ height: 0, width: 0 });
	const [imageDimensions, setImageDimensions] = useState<IDimensions>({ width: 1290, height: 720 });

	const canvasWrapperHeight = useMemo<number>(() => {
		if (canvasDimensions.height > 0) {
			return canvasDimensions.height;
		}

		return (width * imageDimensions.height) / imageDimensions.width;
	}, [canvasDimensions.height, imageDimensions, width]);

	const fontSizes = useMemo(() => {
		const baseWidth = 800; // Base width for reference
		const scaleFactor = Math.max(0.5, Math.min(1.5, canvasDimensions.width / baseWidth));

		return {
			productName: Math.max(10, Math.min(20, 16 * scaleFactor)),
			clientName: Math.max(8, Math.min(16, 14 * scaleFactor)),
			timestamp: Math.max(8, Math.min(14, 12 * scaleFactor)),
			content: Math.max(8, Math.min(14, 12 * scaleFactor))
		};
	}, [canvasDimensions.width]);

	const getScaledCoordinate = (coordinate: ICoordinate, canvasDimensions: IDimensions): ICoordinate => {
		const { height: imageHeight, width: imageWidth } = imageDimensions;
		const { height: canvasHeight, width: canvasWidth } = canvasDimensions;

		return {
			x: (canvasWidth * coordinate.x) / imageWidth,
			y: (canvasHeight * coordinate.y) / imageHeight
		};
	};

	function createTopOverlayElement(canvasWidth: number): HTMLElement {
		const overlay = document.createElement("div");
		overlay.className = "evidence-image-overlay top";
		overlay.style.width = `${canvasWidth}px`;
		overlay.style.position = "absolute";
		overlay.style.top = "-99999px";
		overlay.style.left = "-99999px";
		overlay.style.visibility = "hidden";

		const headerWrapper = document.createElement("div");
		headerWrapper.className = "evidence-image-overlay-header-wrapper";

		const header = document.createElement("div");
		header.className = "evidence-image-overlay-header";

		const productName = document.createElement("h2");
		productName.textContent = overlayDetails.productName;

		const divider1 = document.createElement("div");
		divider1.className = "evidence-image-overlay-header-divider";

		const clientName = document.createElement("h3");
		clientName.textContent = overlayDetails.clientName;

		header.append(productName, divider1, clientName);

		const timestampWrapper = document.createElement("div");
		timestampWrapper.className = "evidence-image-overlay-timestamp-wrapper";

		const date = document.createElement("h4");
		date.textContent = `Date: ${moment(overlayDetails.timestamp).format("YYYY-MM-DD")}`;

		const divider2 = document.createElement("div");
		divider2.className = "evidence-image-overlay-header-divider";

		const time = document.createElement("h4");
		time.textContent = `Time: ${moment(overlayDetails.timestamp).format("HH:mm:ss.SSS")}`;

		timestampWrapper.append(date, divider2, time);

		headerWrapper.append(header, timestampWrapper);

		overlay.appendChild(headerWrapper);

		return overlay;
	}

	function createBottomOverlayElement(canvasWidth: number): HTMLElement {
		const overlay = document.createElement("div");
		overlay.className = "evidence-image-overlay bottom";
		overlay.style.width = `${canvasWidth}px`;
		overlay.style.position = "absolute";
		overlay.style.top = "-99999px";
		overlay.style.left = "-99999px";
		overlay.style.visibility = "hidden";

		const content = document.createElement("div");
		content.className = "evidence-image-overlay-content";

		const items = [
			{ label: "Plate Number", value: overlayDetails.numberPlate },
			// { label: "Vehicle Type", value: overlayDetails.vehicleType },
			{ label: "Location", value: overlayDetails.location },
			{ label: "Type", value: overlayDetails.auth }
		];

		items.forEach((item) => {
			const itemDiv = document.createElement("div");
			itemDiv.className = "evidence-image-overlay-content-item";

			const h5 = document.createElement("h5");
			const labelSpan = document.createElement("span");
			labelSpan.className = "evidence-image-overlay-content-name";
			labelSpan.textContent = `${item.label}: `;
			h5.appendChild(labelSpan);
			h5.appendChild(document.createTextNode(item.value));

			itemDiv.appendChild(h5);
			content.appendChild(itemDiv);
		});

		overlay.appendChild(content);

		return overlay;
	}

	function drawOverlayToCanvas(
		ctx: CanvasRenderingContext2D,
		overlayElement: HTMLElement,
		canvasWidth: number,
		startY: number,
		scaleFactor: number = 1
	): number {
		// Ensure width is set and display is block for proper layout
		overlayElement.style.width = `${canvasWidth}px`;
		overlayElement.style.display = "block";
		overlayElement.style.position = "absolute";
		overlayElement.style.top = "0";
		overlayElement.style.left = "0";
		overlayElement.style.visibility = "visible";
		overlayElement.style.opacity = "1";

		// Scale font sizes in DOM elements to match canvas scaling
		// This ensures layout calculations match what will be drawn
		if (scaleFactor !== 1) {
			const textElements = overlayElement.querySelectorAll("h2, h3, h4, h5, span");
			textElements.forEach((element) => {
				const htmlEl = element as HTMLElement;
				const computedStyle = window.getComputedStyle(htmlEl);
				const baseFontSize = parseFloat(computedStyle.fontSize) || 16;
				htmlEl.style.fontSize = `${baseFontSize * scaleFactor}px`;
			});
		}

		// APpend to body temporarily to get computed styles and positions
		document.body.appendChild(overlayElement);

		// Force multiple reflows to ensure floxbox layout is fully calculated
		void overlayElement.offsetHeight; // Force layout
		void overlayElement.offsetWidth; // Force layout again

		// Access all child elements to force their layout calculation
		const allElements = overlayElement.querySelectorAll("*");
		allElements.forEach((element) => {
			void (element as HTMLElement).offsetHeight;
			void (element as HTMLElement).offsetWidth;
		});

		const overlayHeight = overlayElement.offsetHeight;
		const overlayRect = overlayElement.getBoundingClientRect();

		// Get computed background color
		const computedStyle = window.getComputedStyle(overlayElement);
		const backgroundColor = computedStyle.backgroundColor || "#000000";

		// Draw background
		ctx.fillStyle = backgroundColor;
		ctx.fillRect(0, startY, canvasWidth, overlayHeight);

		// Helper function to draw all text and visual elements
		const drawAllElements = (element: Element) => {
			// Get bounding rect after layout is forced
			const rect = element.getBoundingClientRect();

			// Calculate position relative to overlay element
			const relativeX = rect.left - overlayRect.left;
			const relativeY = rect.top - overlayRect.top;
			const style = window.getComputedStyle(element);

			// Draw divider
			if (element.classList.contains("evidence-image-overlay-header-divider")) {
				ctx.fillStyle = "#ffffff";
				ctx.fillRect(relativeX, startY + relativeY, rect.width, rect.height);
				return; // Don't process children for dividers
			}

			// Draw borders for content items
			if (element.classList.contains("evidence-image-overlay-content-item")) {
				ctx.strokeStyle = "#ffffff";
				ctx.lineWidth = 1;
				ctx.strokeRect(relativeX, startY + relativeY, rect.width, rect.height);
			}

			// Draw text elements (h2, h3, h4, h5) - draw only leaf text nodes
			const isTextElement = ["H2", "H3", "H4", "H5"].includes(element.tagName);
			if (isTextElement) {
				// Check if this element has text children (like nested h2/h3/h4/h5) - if so, skip drawing this one
				const hasNestedTextElements = Array.from(element.children).some((child) =>
					["H2", "H3", "H4", "H5"].includes(child.tagName)
				);

				if (!hasNestedTextElements) {
					ctx.textBaseline = "middle";
					ctx.textAlign = "left";
					// Font size is already scaled in DOM, so use it directly
					const fontSize = parseFloat(style.fontSize) || 16;

					// Calculate vertical center of the element for middle baseline alignment
					const verticalCenter = startY + relativeY + rect.height / 2;

					// Check if there's a span inside (for bold labels in bottom overlay)
					const spanElement = element.querySelector("span.evidence-image-overlay-content-name");
					if (spanElement) {
						// Draw label (bold) first
						const spanStyle = window.getComputedStyle(spanElement);
						const spanRect = spanElement.getBoundingClientRect();
						const spanX = spanRect.left - overlayRect.left;
						const labelText = spanElement.textContent || "";

						ctx.fillStyle = spanStyle.color || "#ffffff";
						const spanFontSize = parseFloat(spanStyle.fontSize) || fontSize;
						ctx.font = `${spanStyle.fontWeight} ${spanFontSize}px ${spanStyle.fontFamily}`;
						ctx.fillText(labelText, spanX, verticalCenter);

						// Draw value (regular) after label
						const labelWidth = ctx.measureText(labelText).width;
						const valueText = element.textContent?.replace(labelText, "").trim() || "";
						if (valueText) {
							ctx.fillStyle = style.color || "#ffffff";
							ctx.font = `${style.fontWeight} ${fontSize}px ${style.fontFamily}`;
							ctx.fillText(valueText, spanX + labelWidth, verticalCenter);
						}
					} else {
						// Draw full text at the element's vertical center
						const text = element.textContent || "";
						if (text.trim()) {
							ctx.fillStyle = style.color || "#ffffff";
							ctx.font = `${style.fontWeight} ${fontSize}px ${style.fontFamily}`;
							ctx.fillText(text, relativeX, verticalCenter);
						}
					}
					return; // Don't process children for text elements we've drawn
				}
			}

			// Recursively process children
			Array.from(element.children).forEach((child) => drawAllElements(child));
		};

		// Draw all elements
		Array.from(overlayElement.children).forEach((child) => drawAllElements(child));

		// Clean up
		document.body.removeChild(overlayElement);

		return overlayHeight;
	}

	function handleDownload() {
		if (!src) return;
		if (!downloadCanvasRef.current) return;

		setShowDownloadButtonLoader(true);

		const canvasContext = downloadCanvasRef.current.getContext("2d");
		if (!canvasContext) return;

		const backgroundImage = new Image();
		backgroundImage.crossOrigin = "anonymous";

		backgroundImage.onload = () => {
			if (!downloadCanvasRef.current) return;

			try {
				// Use original image dimensions
				const originalWidth = backgroundImage.width;
				const originalHeight = backgroundImage.height;

				// Calculate scale factor based on image dimensions
				// Use the average of width and height scaling, or use the larger dimension for better scaling
				const baseWidth = 1280; // Reference width
				// const baseHeight = 720; // Reference height
				const widthScale = originalWidth / baseWidth;
				// const heightScale = originalHeight / baseHeight;
				// Use average scale for balanced scaling, or max for more aggressive scaling
				// const scaleFactor = Math.max(widthScale, heightScale);
				// Use width scale only
				const scaleFactor = widthScale;

				// Helper function to scale fonts in an overlay element
				const scaleOverlayFonts = (overlayElement: HTMLElement, scale: number) => {
					if (scale !== 1) {
						const textElements = overlayElement.querySelectorAll("h2, h3, h4, h5, span");
						textElements.forEach((element) => {
							const htmlEl = element as HTMLElement;
							const computedStyle = window.getComputedStyle(htmlEl);
							const baseFontSize = parseFloat(computedStyle.fontSize) || 16;
							htmlEl.style.fontSize = `${baseFontSize * scale}px`;
						});
					}
				};

				// Create overlay elements matching UI structure
				const topOverlayElement = createTopOverlayElement(originalWidth);
				const bottomOverlayElement = createBottomOverlayElement(originalWidth);

				// Scale fonts before measuring heights to match what will be drawn
				scaleOverlayFonts(topOverlayElement, scaleFactor);
				scaleOverlayFonts(bottomOverlayElement, scaleFactor);

				// Get overlay heights bu temporarily appending them
				// Set up elements for measurement
				topOverlayElement.style.width = `${originalWidth}px`;
				topOverlayElement.style.display = "block";
				topOverlayElement.style.position = "absolute";
				topOverlayElement.style.top = "0";
				topOverlayElement.style.left = "0";
				topOverlayElement.style.visibility = "visible";
				topOverlayElement.style.opacity = "1";

				bottomOverlayElement.style.width = `${originalWidth}px`;
				bottomOverlayElement.style.display = "block";
				bottomOverlayElement.style.position = "absolute";
				bottomOverlayElement.style.top = "0";
				bottomOverlayElement.style.left = "0";
				bottomOverlayElement.style.visibility = "visible";
				bottomOverlayElement.style.opacity = "1";

				document.body.appendChild(topOverlayElement);
				// Force layout calculation on element and all children
				void topOverlayElement.offsetHeight;
				const allTopElements = topOverlayElement.querySelectorAll("*");
				allTopElements.forEach((el) => {
					void (el as HTMLElement).offsetHeight;
					void (el as HTMLElement).offsetWidth;
				});
				const topOverlayHeight = topOverlayElement.offsetHeight;
				document.body.removeChild(topOverlayElement);

				document.body.appendChild(bottomOverlayElement);
				// Force layout calculation on element and all children
				void bottomOverlayElement.offsetHeight;
				const allBottomElements = bottomOverlayElement.querySelectorAll("*");
				allBottomElements.forEach((el) => {
					void (el as HTMLElement).offsetHeight;
					void (el as HTMLElement).offsetWidth;
				});
				const bottomOverlayHeight = bottomOverlayElement.offsetHeight;
				document.body.removeChild(bottomOverlayElement);

				// Calculate total canvas height
				const totalHeight = topOverlayHeight + originalHeight + bottomOverlayHeight;

				// Set canvas dimensions
				downloadCanvasRef.current.width = originalWidth;
				downloadCanvasRef.current.height = totalHeight;

				canvasContext.clearRect(0, 0, originalWidth, totalHeight);

				// Draw top overlay
				let currentY = 0;
				const topOverlayElementForDrawing = createTopOverlayElement(originalWidth);
				drawOverlayToCanvas(canvasContext, topOverlayElementForDrawing, originalWidth, currentY, scaleFactor);
				currentY += topOverlayHeight;

				// Draw image at original dimensions
				canvasContext.drawImage(backgroundImage, 0, currentY, originalWidth, originalHeight);

				// Draw bounding boxes on the image if it's a frame
				if (!src.startsWith("http") && bbox.length > 0) {
					canvasContext.strokeStyle = BBOX_STROKE_COLOR;
					canvasContext.lineWidth = BBOX_STROKE_WIDTH * 2;

					const downloadCanvasDimensions: IDimensions = {
						width: originalWidth,
						height: originalHeight
					};

					for (const bboxItem of bbox) {
						const startCoordinate: ICoordinate = getScaledCoordinate(
							{ x: bboxItem[0], y: bboxItem[1] },
							downloadCanvasDimensions
						);
						const bboxDimensions: ICoordinate = getScaledCoordinate(
							{ x: bboxItem[2], y: bboxItem[3] },
							downloadCanvasDimensions
						);

						canvasContext.strokeRect(
							startCoordinate.x,
							currentY + startCoordinate.y,
							bboxDimensions.x,
							bboxDimensions.y
						);
					}
				}

				// Draw bottom overlay
				currentY += originalHeight;
				const bottomOverlayElementForDrawing = createBottomOverlayElement(originalWidth);
				drawOverlayToCanvas(canvasContext, bottomOverlayElementForDrawing, originalWidth, currentY, scaleFactor);

				// Download the image
				let fileName = `${frameID}.jpeg`;

				if (src.startsWith("http")) {
					const splitURL = src.split("/");
					const splitName = splitURL[splitURL.length - 1]?.split("?");
					fileName = splitName[0];
				}

				downloadImageFromUrl(downloadCanvasRef.current.toDataURL("image/jpeg"), fileName).finally(() => {
					setShowDownloadButtonLoader(false);
				});
			} catch (error) {
				console.error("Error rendering overlays:", error);
				setShowDownloadButtonLoader(false);
			}

			backgroundImage.onload = () => {};
		};

		backgroundImage.src = src;
	}

	useImperativeHandle(ref, () => ({
		downloadImage: handleDownload
	}));

	useEffect(() => {
		if (src && canvasRef.current && canvasRef.current.offsetWidth) {
			setCanvasDimensions((currentDimensions) => ({
				...currentDimensions,
				width: canvasRef.current?.offsetWidth ?? 0
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [src, canvasRef.current]);

	useEffect(() => {
		if (canvasDimensions.width) {
			const canvasHeight = (canvasDimensions.width * imageDimensions.height) / imageDimensions.width;

			setCanvasDimensions((currentDimensions) => ({
				...currentDimensions,
				height: canvasHeight
			}));
		}
	}, [canvasDimensions.width, imageDimensions, src]);

	useEffect(() => {
		if (canvasRef.current && src) {
			// eslint-disable-next-line no-self-assign
			canvasRef.current.width = canvasRef.current.width;

			const canvasContext = canvasRef.current.getContext("2d");
			if (!canvasContext) return;

			canvasContext.strokeStyle = BBOX_STROKE_COLOR;
			canvasContext.lineWidth = BBOX_STROKE_WIDTH;

			for (const bboxItem of bbox) {
				const startCoordinate = getScaledCoordinate({ x: bboxItem[0], y: bboxItem[1] }, canvasDimensions);
				const bboxDimensions = getScaledCoordinate({ x: bboxItem[2], y: bboxItem[3] }, canvasDimensions);

				canvasContext.strokeRect(startCoordinate.x, startCoordinate.y, bboxDimensions.x, bboxDimensions.y);
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bbox, canvasDimensions, src]);

	useEffect(() => {
		if (src) {
			const img = new Image();
			img.onerror = () => {
				setShowError(true);
			};
			img.onload = () => {
				setShowError(false);
				setImageDimensions({
					width: img.width,
					height: img.height
				});
			};
			img.src = src;
		}
	}, [src]);

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					height: "100%",
					aspectRatio: "16/9"
				}}
			>
				<CircularProgress size={30} />
			</Box>
		);
	}

	if (showError || !src) {
		if (archived) {
			return (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						height: "100%",
						aspectRatio: "16/9",
						backgroundImage: `url(${ArchivedImage})`,
						backgroundSize: "contain",
						backgroundPosition: "center",
						backgroundRepeat: "no-repeat",
						gap: "0.5rem"
					}}
				>
					<LockIcon sx={{ color: "white" }} />
					<Typography variant="h6" color="white">
						Upgrade to paid version to see images
					</Typography>
				</Box>
			);
		}

		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					height: "100%",
					aspectRatio: "16/9"
				}}
			>
				<NoImageIcon width={100} fill="#d5d5d5" stroke="#d5d5d5" />
				<Typography variant="h6" color="#999999">
					Image not available
				</Typography>
			</Box>
		);
	}

	return (
		<Box className="evidence-image-wrapper">
			<Box className="evidence-image-overlay top" sx={{ padding: { xs: "8px 12px", sm: "16px 24px" } }}>
				<Box className="evidence-image-overlay-header-wrapper">
					<Box className="evidence-image-overlay-header">
						<h2 style={{ fontSize: `${fontSizes.productName}px` }}>{overlayDetails.productName}</h2>
						<Box className="evidence-image-overlay-header-divider" />
						<h3 style={{ fontSize: `${fontSizes.clientName}px` }}>{overlayDetails.clientName}</h3>
					</Box>

					<Box className="evidence-image-overlay-timestamp-wrapper">
						<h4 style={{ fontSize: `${fontSizes.timestamp}px` }}>{`Date: ${moment(overlayDetails.timestamp).format(
							"YYYY-MM-DD"
						)}`}</h4>
						<Box className="evidence-image-overlay-header-divider" />
						<h4 style={{ fontSize: `${fontSizes.timestamp}px` }}>{`Time: ${moment(overlayDetails.timestamp).format(
							"hh:mm A"
						)}`}</h4>
					</Box>
				</Box>
			</Box>

			<Box sx={{ width: "100%", height: canvasWrapperHeight, position: "relative" }}>
				<canvas
					ref={canvasRef}
					width={canvasDimensions.width}
					height={canvasDimensions.height}
					style={{
						width: "100%",
						backgroundSize: "contain",
						backgroundPosition: "center",
						backgroundRepeat: "no-repeat",
						backgroundImage: `url(${String(src)})`
					}}
				/>

				{openFullscreen ? (
					<IconButton
						disableRipple
						size="small"
						className="image-action-button right"
						onClick={openFullscreen}
						sx={{
							padding: { xs: "4px", sm: "8px" },
							"& .MuiSvgIcon-root": {
								fontSize: { xs: "1.25rem", sm: "1.5rem" }
							}
						}}
					>
						<FullscreenIcon sx={{ color: "white" }} fontSize="large" />
					</IconButton>
				) : null}
			</Box>

			<Box className="evidence-image-overlay bottom" sx={{ padding: { xs: "8px 12px", sm: "16px 24px" } }}>
				<Box className="evidence-image-overlay-content">
					<Box className="evidence-image-overlay-content-item" sx={{ flexShrink: 0 }}>
						<h5 style={{ whiteSpace: "nowrap", fontSize: `${fontSizes.content}px` }}>
							<span className="evidence-image-overlay-content-name">Plate Number: </span>
							{overlayDetails.numberPlate}
						</h5>
					</Box>

					{/* <Box className="evidence-image-overlay-content-item">
						<h5 style={{ fontSize: `${fontSizes.content}px` }}>
							<span className="evidence-image-overlay-content-name">Vehicle Type: </span>
							{overlayDetails.vehicleType}
						</h5>
					</Box> */}

					<Box className="evidence-image-overlay-content-item">
						<h5 style={{ fontSize: `${fontSizes.content}px` }}>
							<span className="evidence-image-overlay-content-name">Location: </span>
							{overlayDetails.location}
						</h5>
					</Box>

					<Box className="evidence-image-overlay-content-item">
						<h5 style={{ fontSize: `${fontSizes.content}px` }}>
							<span className="evidence-image-overlay-content-name">Type: </span>
							{overlayDetails.auth}
						</h5>
					</Box>
				</Box>
			</Box>

			<canvas
				ref={downloadCanvasRef}
				style={{
					width: "100%",
					backgroundSize: "contain",
					backgroundPosition: "center",
					backgroundRepeat: "no-repeat",
					backgroundImage: `url(${String(src)})`,
					position: "absolute",
					top: -99999,
					left: -99999,
					opacity: 0
				}}
			/>
		</Box>
	);
});

const LogDetailsDialog: FC<ILogDetailsDialogProps> = (props) => {
	const {
		open,
		eventLogDetails,
		previousButtonDisabled,
		nextButtonDisabled,
		pageChangeLoading,
		tatEnabled,
		onClose,
		onPrevious,
		onNext,
		onEditEventSuccess
	} = props;

	const commentInputRef = useRef<HTMLInputElement>(null);
	const frameImageRef = useRef<IFrameImageRef>(null);

	const authState = useAppSelector((state) => state.auth);

	const [showFullscreenImage, setShowFullscreenImage] = useState<boolean>(false);
	const [selectedTab, setSelectedTab] = useState<LOG_DETAILS_TAB_E>(LOG_DETAILS_TAB_E.IMAGE);
	const [filters, setFilters] = useState<IGetTurnaroundLogsRequestData>({
		number_plate: "",
		page: 1
	});
	const [latestEventImage, setLatestEventImage] = useState<{ src: string; archived: boolean }>({
		src: "",
		archived: false
	});
	const [commentEditingEnabled, setCommentEditingEnabled] = useState<boolean>(false);
	const [commentInput, setCommentInput] = useState<string>("");

	const [touchStart, setTouchStart] = useState<number | null>(null);
	const [touchEnd, setTouchEnd] = useState<number | null>(null);
	const [componentWidth, setComponentWidth] = useState<number>(0);
	const [showDownloadButtonLoader, setShowDownloadButtonLoader] = useState<boolean>(false);

	// APIS
	// GET TURNAROUND LOGS
	const {
		data: getTurnaroundLogsResponse,
		isLoading: getTurnaroundLogsLoading,
		isFetching: getTurnaroundLogsFetching,
		originalArgs: getTurnaroundLogsArgs
	} = useGetTurnaroundLogsQuery(filters, { skip: !filters.number_plate });

	// EDIT EVENT LOG
	const [
		editEventLog,
		{
			isLoading: editEventLogLoading,
			error: editEventLogError,
			data: editEventLogResponse,
			reset: resetEditEventLogState
		}
	] = useEditEventLogMutation();

	const { data: getSystemIntegratorDetailsResponse } = useGetSystemIntegratorDetailsQuery(null, {
		skip: authState.user.role === USER_ROLES_E.SYSTEM_INTEGRATOR
	});

	const { data: getSocietyConfigResponse } = useGetSocietyConfigQuery(null, { refetchOnMountOrArgChange: true });

	const societyDetails: ISocietyConfigDetails | null =
		getSocietyConfigResponse && getSocietyConfigResponse.results.length > 0
			? getSocietyConfigResponse.results[0]
			: null;

	const systemIntegratorDetails = getSystemIntegratorDetailsResponse ? getSystemIntegratorDetailsResponse[0] : null;

	const TurnaroundLogsRowsData = useMemo<ITurnaroundLogsRowDetails[]>(() => {
		const rows: ITurnaroundLogsRowDetails[] = [];

		if (getTurnaroundLogsResponse) {
			for (const logItem of getTurnaroundLogsResponse.results) {
				rows.push({
					id: logItem.id,
					entry_timestamp: logItem.entry_event
						? moment(logItem.entry_event.timestamp).format("YYYY-MM-DD HH:mm:ss")
						: "--",
					exit_timestamp: logItem.exit_event
						? moment(logItem.exit_event.timestamp).format("YYYY-MM-DD HH:mm:ss")
						: "--",
					time_spent: logItem.stay_duration ? getFormattedDuration(logItem.stay_duration) : "--"
				});
			}
		}

		return rows;
	}, [getTurnaroundLogsResponse]);

	function getSocietyName(): string {
		if (authState.user.role === USER_ROLES_E.SYSTEM_INTEGRATOR) {
			if (!systemIntegratorDetails) return "";
			return systemIntegratorDetails.name;
		}

		if (!societyDetails || !societyDetails.name) return "";

		let societyName = societyDetails.name;

		// if (societyDetails.city) societyName = `${societyName}, ${societyDetails.city}`;

		return societyName;
	}

	function handleShowFullscreenImage() {
		setShowFullscreenImage(true);
	}

	function handleHideFullscreenImage() {
		setShowFullscreenImage(false);
	}

	const onTouchStart = (e: React.TouchEvent) => {
		setTouchEnd(null);
		setTouchStart(e.targetTouches[0].clientX);
	};

	const onTouchMove = (e: React.TouchEvent) => {
		setTouchEnd(e.targetTouches[0].clientX);
	};

	const onTouchEnd = () => {
		if (!touchStart || !touchEnd) return;

		const distance = touchStart - touchEnd;
		const isLeftSwipe = distance > minSwipeDistance;
		const isRightSwipe = distance < -minSwipeDistance;

		if (isLeftSwipe && !nextButtonDisabled) {
			onNext(eventLogDetails?.unique_id ?? "");
		}
		if (isRightSwipe && !previousButtonDisabled) {
			onPrevious(eventLogDetails?.unique_id ?? "");
		}
	};

	function handleDownloadImage() {
		// if (eventLogDetails && eventLogDetails.unique_id && eventLogDetails.frame_image) {
		// 	downloadImageFromUrl(eventLogDetails.frame_image, `Evidence-${eventLogDetails.unique_id}.png`);
		// }
		if (frameImageRef.current) {
			frameImageRef.current.downloadImage();
		}
	}

	function handleChangeSelectedTab(updatedValue: LOG_DETAILS_TAB_E) {
		setSelectedTab(updatedValue);
	}

	function handlePageChange(event: GridPaginationModel) {
		setFilters((currentFilters) => ({
			...currentFilters,
			page: event.page + 1
		}));
	}

	function handleEnableCommentEditing() {
		setCommentEditingEnabled(true);
		setCommentInput(eventLogDetails?.comment ?? "");
	}

	function handleDisableCommentEditing() {
		setCommentEditingEnabled(false);
		setCommentInput("");
	}

	function handleChangeCommentInput(event: ChangeEvent<HTMLInputElement>) {
		setCommentInput(event.target.value);
	}

	function handleSaveComment() {
		if (eventLogDetails && eventLogDetails.id) {
			editEventLog({
				id: eventLogDetails.id,
				data: { comment: commentInput || null }
			});
		}
	}

	const keyPressEventListener = useCallback(
		(event: KeyboardEvent) => {
			if (open) {
				if (event.key === "ArrowLeft") {
					if (document.activeElement !== commentInputRef.current) {
						event.preventDefault();
						onPrevious(eventLogDetails?.unique_id ?? "");
					}
				} else if (event.key === "ArrowRight") {
					if (document.activeElement !== commentInputRef.current) {
						event.preventDefault();
						onNext(eventLogDetails?.unique_id ?? "");
					}
				} else if (event.key === "Escape") {
					if (showFullscreenImage) {
						handleHideFullscreenImage();
					}
				}
			}
		},
		[eventLogDetails?.unique_id, onNext, onPrevious, open, showFullscreenImage]
	);

	useEffect(() => {
		document.addEventListener("keydown", keyPressEventListener, false);
		return () => {
			document.removeEventListener("keydown", keyPressEventListener, false);
		};
	}, [keyPressEventListener]);

	useEffect(() => {
		if (!open) {
			setSelectedTab(LOG_DETAILS_TAB_E.IMAGE);
			handleDisableCommentEditing();
		}
	}, [open]);

	useEffect(() => {
		if (eventLogDetails) {
			setFilters({
				number_plate: eventLogDetails.vehicle_number,
				page: 1
			});
		}
	}, [eventLogDetails]);

	useEffect(() => {
		if (eventLogDetails && getTurnaroundLogsArgs && getTurnaroundLogsResponse) {
			setLatestEventImage((currentImage) => {
				if (getTurnaroundLogsArgs.number_plate !== eventLogDetails.vehicle_number) {
					return { src: "", archived: false };
				}

				if (getTurnaroundLogsArgs.page !== 1) {
					return currentImage;
				}

				if (getTurnaroundLogsResponse.results.length > 0) {
					const latestEvent = getTurnaroundLogsResponse.results[0];

					if (latestEvent.exit_event) {
						return {
							src: latestEvent.exit_event.frame_img_uri ?? "",
							archived: latestEvent.exit_event.bucket_tier === "archive"
						};
					}

					if (latestEvent.entry_event) {
						return {
							src: latestEvent.entry_event.frame_img_uri ?? "",
							archived: latestEvent.entry_event.bucket_tier === "archive"
						};
					}
				}

				return { src: "", archived: false };
			});
		}
	}, [eventLogDetails, getTurnaroundLogsArgs, getTurnaroundLogsResponse]);

	useEffect(() => {
		if (editEventLogResponse) {
			resetEditEventLogState();
			handleDisableCommentEditing();
			onEditEventSuccess(editEventLogResponse);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editEventLogResponse]);

	const columnsData: GridColDef[] = [
		{
			field: "entry_timestamp",
			headerName: "Entry",
			flex: 1,
			minWidth: 140,
			sortable: false,
			align: "center",
			headerAlign: "center"
		},
		{
			field: "exit_timestamp",
			headerName: "Exit",
			flex: 1,
			minWidth: 140,
			sortable: false,
			align: "center",
			headerAlign: "center"
		},
		{
			field: "time_spent",
			headerName: "Time Spent",
			flex: 1,
			minWidth: 140,
			sortable: false,
			align: "center",
			headerAlign: "center"
		}
	];

	const TabRenderer: LogDetailsTabRendererType = {
		[LOG_DETAILS_TAB_E.IMAGE]: eventLogDetails ? (
			<>
				<Grid item xs={12} md={8} lg={9}>
					<Card
						sx={{
							background: "linear-gradient(135deg, #fff 0%, #f8f9ff 100%)",
							border: "1px solid rgba(91, 78, 191, 0.08)",
							borderRadius: "8px",
							boxShadow: "0px 1px 4px rgba(91, 78, 191, 0.04)",
							padding: 0,
							marginBottom: 0
						}}
					>
						<CardContent sx={{ padding: { xs: "12px 12px 6px 12px !important", sm: "16px 16px 8px 16px !important" } }}>
							<Grid container alignItems="center" spacing={1} sx={{ marginBottom: 1 }}>
								<Grid item xs={12} md={4} lg={3}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Box
											sx={{
												width: "3px",
												height: "18px",
												background: "linear-gradient(135deg, #5b4ebf 0%, #403685 100%)",
												borderRadius: "2px"
											}}
										/>
										<Typography
											variant="subtitle1"
											sx={{
												fontWeight: 600,
												color: "#2c3e50",
												fontSize: { xs: "0.875rem", sm: "1rem" },
												letterSpacing: 0.5
											}}
										>
											Event Details
										</Typography>
									</Box>
								</Grid>
							</Grid>

							<Grid container spacing={{ xs: 1, sm: 1 }}>
								<Grid item xs={12} md={6} lg={4}>
									<Box className="log-detail-wrapper">
										<Box className="log-detail-icon">
											<PersonIcon color="primary" />
										</Box>

										<Box className="log-detail">
											<Typography classes={{ root: "log-detail-title" }}>Name</Typography>
											<Typography classes={{ root: "log-detail-value" }}>{eventLogDetails.name}</Typography>
										</Box>
									</Box>
								</Grid>

								{eventLogDetails.type === "visitor" && eventLogDetails.contact_number ? (
									<Grid item xs={12} md={6} lg={4}>
										<Box className="log-detail-wrapper">
											<Box className="log-detail-icon">
												<NumbersIcon color="primary" />
											</Box>

											<Box className="log-detail">
												<Typography classes={{ root: "log-detail-title" }}>Contact Number</Typography>
												<Typography classes={{ root: "log-detail-value" }}>{eventLogDetails.contact_number}</Typography>
											</Box>
										</Box>
									</Grid>
								) : null}

								{eventLogDetails.camera_name ? (
									<Grid item xs={12} md={6} lg={4}>
										<Box className="log-detail-wrapper">
											<Box className="log-detail-icon">
												<LocationOnIcon color="primary" />
											</Box>

											<Box className="log-detail">
												<Typography classes={{ root: "log-detail-title" }}>Location</Typography>
												<Typography classes={{ root: "log-detail-value" }}>{eventLogDetails.camera_name}</Typography>
											</Box>
										</Box>
									</Grid>
								) : null}

								<Grid item xs={12} md={6} lg={4}>
									<Box className="log-detail-wrapper">
										<Box className="log-detail-icon">
											<NumbersIcon color="primary" />
										</Box>

										<Box className="log-detail">
											<Typography classes={{ root: "log-detail-title" }}>Plate Number</Typography>
											<Typography classes={{ root: "log-detail-value" }}>{eventLogDetails.vehicle_number}</Typography>
										</Box>
									</Box>
								</Grid>

								{eventLogDetails.flat_number ? (
									<Grid item xs={12} md={6} lg={4}>
										<Box className="log-detail-wrapper">
											<Box className="log-detail-icon">
												<SquareIcon color="primary" />
											</Box>

											<Box className="log-detail">
												<Typography classes={{ root: "log-detail-title" }}>Flat Number</Typography>
												<Typography classes={{ root: "log-detail-value" }}>{eventLogDetails.flat_number}</Typography>
											</Box>
										</Box>
									</Grid>
								) : null}

								{eventLogDetails.type === "visitor" && eventLogDetails.address ? (
									<Grid item xs={12} md={6} lg={4}>
										<Box className="log-detail-wrapper">
											<Box className="log-detail-icon">
												<LocationOnIcon color="primary" />
											</Box>

											<Box className="log-detail">
												<Typography classes={{ root: "log-detail-title" }}>Address</Typography>
												<Typography classes={{ root: "log-detail-value" }}>{eventLogDetails.address}</Typography>
											</Box>
										</Box>
									</Grid>
								) : null}

								{eventLogDetails.custom_field_data && Object.keys(eventLogDetails.custom_field_data).length > 0
									? Object.entries(eventLogDetails.custom_field_data).map(([key, value]) =>
											value ? (
												<Grid item xs={12} md={6} lg={4}>
													<Box className="log-detail-wrapper">
														<Box className="log-detail-icon">
															<DescriptionIcon color="primary" />
														</Box>

														<Box className="log-detail">
															<Typography classes={{ root: "log-detail-title" }}>{key}</Typography>
															<Typography classes={{ root: "log-detail-value" }}>{value}</Typography>
														</Box>
													</Box>
												</Grid>
											) : null
									  )
									: null}

								<Grid item xs={12} md={6} lg={4}>
									<Box className="log-detail-wrapper">
										<Box className="log-detail-icon">
											<AccessTimeFilledIcon color="primary" />
										</Box>

										<Box className="log-detail">
											<Typography classes={{ root: "log-detail-title" }}>Timestamp</Typography>
											<Typography classes={{ root: "log-detail-value" }}>
												{moment(eventLogDetails.timestamp).format("DD MMM YYYY, hh:mm A")}
											</Typography>
										</Box>
									</Box>
								</Grid>

								<Grid item xs={12} md={6} lg={4}>
									<Box className="log-detail-wrapper">
										<Box className="log-detail-icon">
											<RememberMeIcon color="primary" />
										</Box>

										<Box className="log-detail">
											<Typography classes={{ root: "log-detail-title" }}>Type</Typography>
											<Typography classes={{ root: "log-detail-value" }}>
												{convertToTitleCase(eventLogDetails.type)}
											</Typography>
										</Box>
									</Box>
								</Grid>

								{eventLogDetails.comment || commentEditingEnabled ? (
									<Grid item xs={12}>
										<Typography fontWeight={600} marginBottom="0.5rem">
											Comment
										</Typography>

										{commentEditingEnabled ? (
											<TextField
												fullWidth
												variant="outlined"
												placeholder="Write a comment..."
												multiline
												minRows={4}
												value={commentInput}
												onChange={handleChangeCommentInput}
												autoFocus
												inputRef={commentInputRef}
											/>
										) : (
											<Typography whiteSpace="pre-wrap">{eventLogDetails.comment}</Typography>
										)}
									</Grid>
								) : null}

								{editEventLogError ? (
									<Grid item xs={12}>
										<Typography variant="body2" color="var(--color-error-main)">
											{"data" in editEventLogError && editEventLogError.data
												? String(editEventLogError.data)
												: "Something went wrong"}
										</Typography>
									</Grid>
								) : null}

								<Grid item xs={12}>
									{commentEditingEnabled ? (
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												marginY: "1rem",
												gap: "1rem"
											}}
										>
											<Button
												variant="outlined"
												color="error"
												startIcon={<CloseIcon />}
												onClick={handleDisableCommentEditing}
											>
												Cancel
											</Button>

											<LoadingButton
												disableElevation
												variant="contained"
												color="success"
												startIcon={<SaveIcon />}
												loading={editEventLogLoading}
												loadingPosition="start"
												onClick={handleSaveComment}
											>
												Save
											</LoadingButton>
										</Box>
									) : (
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												marginY: "1rem",
												gap: "1rem"
											}}
										>
											<Button
												variant="outlined"
												color="primary"
												startIcon={<AddCommentIcon />}
												onClick={handleEnableCommentEditing}
											>
												{eventLogDetails.comment ? "Edit Comment" : "Add Comment"}
											</Button>

											<LoadingButton
												variant="contained"
												color="primary"
												startIcon={<DownloadIcon />}
												sx={{ paddingX: 3 }}
												onClick={handleDownloadImage}
												disabled={
													(selectedTab === LOG_DETAILS_TAB_E.IMAGE && !eventLogDetails.frame_image) ||
													(selectedTab === LOG_DETAILS_TAB_E.HISTORY && !latestEventImage) ||
													false
												}
												loading={showDownloadButtonLoader}
												loadingPosition="start"
											>
												Download
											</LoadingButton>
										</Box>
									)}
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>

				{eventLogDetails.plate_image ? (
					<Grid item xs={12} md={4} lg={3}>
						<Card
							sx={{
								background: "linear-gradient(135deg, #fff 0%, #f8f9ff 100%)",
								border: "1px solid rgba(91, 78, 191, 0.08)",
								borderRadius: "8px",
								boxShadow: "0px 1px 4px rgba(91, 78, 191, 0.04)",
								padding: 0,
								marginBottom: 0,
								display: "flex",
								flexDirection: "column",
								// alignItems: "center",
								// justifyContent: "center",
								minHeight: { xs: 120, sm: 180 },
								height: "100%"
							}}
						>
							<CardContent
								sx={{
									width: "100%",
									display: "flex",
									flexDirection: "column",
									// alignItems: "center",
									// justifyContent: "center",
									p: { xs: 1.5, sm: 2 },
									flex: 1
								}}
							>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<Box
										sx={{
											width: "3px",
											height: "18px",
											background: "linear-gradient(135deg, #5b4ebf 0%, #403685 100%)",
											borderRadius: "2px"
										}}
									/>
									<Typography
										variant="subtitle1"
										sx={{
											fontWeight: 600,
											color: "#2c3e50",
											fontSize: { xs: "0.875rem", sm: "1rem" },
											letterSpacing: 0.5
										}}
									>
										Plate Image
									</Typography>
								</Box>

								<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
									<Box
										component="img"
										src={eventLogDetails.plate_image}
										alt="Plate"
										sx={{
											width: "100%",
											// maxWidth: 180,
											maxHeight: { xs: 40, sm: 60 },
											borderRadius: "6px",
											border: "1px solid #e0e0e0",
											objectFit: "contain",
											background: "#fff",
											boxShadow: "0 1px 4px rgba(91,78,191,0.06)",
											flex: 1
										}}
									/>
								</Box>
							</CardContent>
						</Card>
					</Grid>
				) : null}
			</>
		) : null,
		[LOG_DETAILS_TAB_E.HISTORY]: eventLogDetails ? (
			<>
				{/* Vehicle Header Section */}
				<Grid item xs={12}>
					<Card
						sx={{
							background: "#ffffff",
							border: "1px solid rgba(91, 78, 191, 0.08)",
							borderRadius: "8px",
							boxShadow: "0px 2px 8px rgba(91, 78, 191, 0.04)",
							padding: 0,
							marginBottom: 0
						}}
					>
						<CardContent sx={{ padding: { xs: "12px", sm: "16px" } }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
								<Box
									sx={{
										width: "3px",
										height: "18px",
										background: "linear-gradient(135deg, #5b4ebf 0%, #403685 100%)",
										borderRadius: "2px"
									}}
								/>
								<Typography
									variant="subtitle1"
									sx={{
										fontWeight: 600,
										color: "#2c3e50",
										fontSize: { xs: "0.875rem", sm: "1rem" },
										letterSpacing: 0.5
									}}
								>
									Vehicle History
								</Typography>
							</Box>

							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1.5,
									marginTop: 1.5,
									padding: "10px 12px",
									background: "rgba(91, 78, 191, 0.04)",
									borderRadius: "6px",
									border: "1px solid rgba(91, 78, 191, 0.08)"
								}}
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										width: "32px",
										height: "32px",
										background: "linear-gradient(135deg, #5b4ebf 0%, #403685 100%)",
										borderRadius: "6px",
										boxShadow: "0px 1px 4px rgba(91, 78, 191, 0.2)"
									}}
								>
									<VehicleTypeIcon vehicleType={eventLogDetails.vehicle_type} width={16} fill="white" />
								</Box>

								<Box sx={{ flex: 1 }}>
									<Typography
										variant="body2"
										sx={{
											color: "#5b4ebf",
											fontWeight: 500,
											fontSize: "0.7rem",
											textTransform: "uppercase",
											letterSpacing: 0.5,
											marginBottom: 0.25
										}}
									>
										Vehicle Number
									</Typography>
									<Typography
										variant="subtitle1"
										sx={{
											fontWeight: 600,
											color: "#2c3e50",
											fontSize: { xs: "1rem", sm: "1.125rem" }
										}}
									>
										{eventLogDetails.vehicle_number}
									</Typography>
								</Box>
							</Box>
						</CardContent>
					</Card>
				</Grid>

				{/* Statistics Cards */}
				{getTurnaroundLogsResponse ? (
					<>
						<Grid item xs={12} md={6}>
							<Card
								sx={{
									background: "#ffffff",
									border: "1px solid #e8f5e8",
									borderRadius: "8px",
									boxShadow: "0px 2px 8px rgba(76, 175, 80, 0.06)",
									padding: 0,
									height: "100%",
									transition: "all 0.2s ease-in-out",
									"&:hover": {
										borderColor: "#4caf50",
										boxShadow: "0px 4px 16px rgba(76, 175, 80, 0.12)"
									}
								}}
							>
								<CardContent sx={{ padding: { xs: "12px !important", sm: "16px !important" } }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												width: "36px",
												height: "36px",
												background: "var(--color-success-light)",
												borderRadius: "8px"
												// boxShadow: "0px 2px 6px rgba(76, 175, 80, 0.2)"
											}}
										>
											{eventLogDetails.vehicle_type === VEHICLE_TYPE_E.TWO_WHEELER ? (
												<BikeEntryIcon width={18} fill="white" />
											) : (
												<CarEntryIcon width={18} fill="white" />
											)}
										</Box>
										<Box sx={{ flex: 1 }}>
											<Typography
												variant="body2"
												sx={{
													color: "#2e7d32",
													fontWeight: 500,
													fontSize: "0.7rem",
													textTransform: "uppercase",
													letterSpacing: 0.5,
													marginBottom: 0.25
												}}
											>
												Entry Events
											</Typography>
											<Typography
												variant="h5"
												sx={{
													fontWeight: 700,
													color: "#1b5e20",
													fontSize: { xs: "1.25rem", sm: "1.5rem" },
													lineHeight: 1.2
												}}
											>
												{getTurnaroundLogsResponse.entry_count}
											</Typography>
										</Box>
									</Box>
								</CardContent>
							</Card>
						</Grid>

						<Grid item xs={12} md={6}>
							<Card
								sx={{
									background: "#ffffff",
									border: "1px solid #ffebee",
									borderRadius: "8px",
									boxShadow: "0px 2px 8px rgba(244, 67, 54, 0.06)",
									padding: 0,
									height: "100%",
									transition: "all 0.2s ease-in-out",
									"&:hover": {
										borderColor: "#f44336",
										boxShadow: "0px 4px 16px rgba(244, 67, 54, 0.12)"
									}
								}}
							>
								<CardContent sx={{ padding: { xs: "12px !important", sm: "16px !important" } }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												width: "36px",
												height: "36px",
												background: "var(--color-error-light)",
												borderRadius: "8px"
												// boxShadow: "0px 2px 6px rgba(244, 67, 54, 0.2)"
											}}
										>
											{eventLogDetails.vehicle_type === VEHICLE_TYPE_E.TWO_WHEELER ? (
												<BikeExitIcon width={18} fill="white" />
											) : (
												<CarExitIcon width={18} fill="white" />
											)}
										</Box>
										<Box sx={{ flex: 1 }}>
											<Typography
												variant="body2"
												sx={{
													color: "#c62828",
													fontWeight: 500,
													fontSize: "0.7rem",
													textTransform: "uppercase",
													letterSpacing: 0.5,
													marginBottom: 0.25
												}}
											>
												Exit Events
											</Typography>
											<Typography
												variant="h5"
												sx={{
													fontWeight: 700,
													color: "#b71c1c",
													fontSize: { xs: "1.25rem", sm: "1.5rem" },
													lineHeight: 1.2
												}}
											>
												{getTurnaroundLogsResponse.exit_count}
											</Typography>
										</Box>
									</Box>
								</CardContent>
							</Card>
						</Grid>
					</>
				) : null}

				{/* History Table */}
				<Grid item xs={12}>
					<Card
						sx={{
							background: "#ffffff",
							border: "1px solid rgba(91, 78, 191, 0.08)",
							borderRadius: "8px",
							boxShadow: "0px 2px 8px rgba(91, 78, 191, 0.04)",
							padding: 0,
							marginTop: 1
						}}
					>
						<CardContent sx={{ padding: { xs: "12px", sm: "16px" } }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, marginBottom: 1.5 }}>
								<Box
									sx={{
										width: "3px",
										height: "18px",
										background: "linear-gradient(135deg, #5b4ebf 0%, #403685 100%)",
										borderRadius: "2px"
									}}
								/>
								<Typography
									variant="subtitle1"
									sx={{
										fontWeight: 600,
										color: "#2c3e50",
										fontSize: { xs: "0.875rem", sm: "1rem" },
										letterSpacing: 0.5
									}}
								>
									Turnaround History
								</Typography>
							</Box>

							<DataGrid
								columns={columnsData}
								rows={TurnaroundLogsRowsData}
								loading={getTurnaroundLogsLoading || getTurnaroundLogsFetching}
								disableColumnMenu
								disableRowSelectionOnClick
								density="compact"
								rowSpacingType="border"
								columnHeaderHeight={48}
								paginationMode="server"
								pageSizeOptions={[PAGINATION_CONSTANTS.ROWS_PER_PAGE]}
								onPaginationModelChange={handlePageChange}
								rowCount={getTurnaroundLogsResponse?.count ?? 0}
								paginationModel={{
									page: filters.page ? filters.page - 1 : 0,
									pageSize: PAGINATION_CONSTANTS.ROWS_PER_PAGE
								}}
								hideFooter={(getTurnaroundLogsResponse?.count ?? 0) <= PAGINATION_CONSTANTS.ROWS_PER_PAGE}
								sx={{
									border: "1px solid rgba(91, 78, 191, 0.12)",
									borderRadius: "6px",
									"& .MuiDataGrid-columnHeaders": {
										background: "#f8f9fa",
										borderBottom: "1px solid rgba(91, 78, 191, 0.1)"
									},
									"& .MuiDataGrid-columnHeader": {
										fontWeight: 600,
										color: "#5b4ebf",
										fontSize: "0.8rem"
									},
									"& .MuiDataGrid-row:hover": {
										background: "rgba(91, 78, 191, 0.04)"
									}
								}}
								classes={{
									columnSeparator: "log-history-table-column-separator",
									columnHeader: "log-history-table-column-header",
									virtualScroller: "log-history-table-virtual-scroller",
									cell: "log-history-table-cell"
								}}
							/>
						</CardContent>
					</Card>
				</Grid>
			</>
		) : null
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" disableEscapeKeyDown={showFullscreenImage}>
			<DialogTitle
				textAlign="center"
				sx={{
					background: "linear-gradient(135deg, #5b4ebf 0%, #403685 100%)",
					color: "white",
					position: "relative",
					padding: { xs: "12px 16px 8px 16px", sm: "16px 20px 12px 20px" },
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
				}}
			>
				Log Details
				<Box sx={{ position: "absolute", top: { xs: 8, sm: 12 }, right: { xs: 8, sm: 12 } }}>
					<IconButton
						onClick={onClose}
						sx={{
							color: "white",
							background: "rgba(255, 255, 255, 0.1)",
							backdropFilter: "blur(8px)",
							borderRadius: "50%",
							padding: { xs: "4px", sm: "6px" },
							"&:hover": {
								background: "rgba(255, 255, 255, 0.2)",
								transform: "scale(1.1)"
							},
							transition: "all 0.2s ease-in-out"
						}}
					>
						<CloseIcon sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }} />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent
				onTouchStart={onTouchStart}
				onTouchMove={onTouchMove}
				onTouchEnd={onTouchEnd}
				sx={{
					padding: { xs: "16px !important", sm: "20px !important" },
					background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)"
				}}
			>
				{pageChangeLoading ? (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
						<CircularProgress />
					</Box>
				) : eventLogDetails ? (
					<Grid container spacing={{ xs: 1, sm: 2 }}>
						{tatEnabled ? (
							<Grid item xs={12}>
								<Box className="log-details-tabs-wrapper">
									<Box
										className={`log-details-tab-button ${selectedTab === LOG_DETAILS_TAB_E.IMAGE ? "active" : ""}`}
										onClick={() => handleChangeSelectedTab(LOG_DETAILS_TAB_E.IMAGE)}
									>
										<Typography variant="subtitle2" fontWeight={500}>
											Image
										</Typography>
									</Box>

									<Box
										className={`log-details-tab-button ${selectedTab === LOG_DETAILS_TAB_E.HISTORY ? "active" : ""}`}
										onClick={() => handleChangeSelectedTab(LOG_DETAILS_TAB_E.HISTORY)}
									>
										<Typography variant="subtitle2" fontWeight={500}>
											History
										</Typography>
									</Box>
								</Box>
							</Grid>
						) : null}

						<Grid item xs={12}>
							<Box className="log-details-dialog-header">
								<Box
									className={`log-change-arrow ${previousButtonDisabled ? "disabled" : ""}`}
									onClick={() => onPrevious(eventLogDetails.unique_id)}
									sx={{
										background: "linear-gradient(135deg, rgba(91, 78, 191, 0.1) 0%, rgba(91, 78, 191, 0.05) 100%)",
										border: "1px solid rgba(91, 78, 191, 0.2)",
										borderRadius: "8px",
										transition: "all 0.2s ease-in-out",
										"&:hover": {
											background: "linear-gradient(135deg, rgba(91, 78, 191, 0.15) 0%, rgba(91, 78, 191, 0.08) 100%)",
											transform: "scale(1.05)"
										},
										display: { xs: "none", sm: "flex" }
									}}
								>
									<ChevronLeftIcon
										color={previousButtonDisabled ? "disabled" : "primary"}
										sx={{ fontSize: { xs: "32px", sm: "48px" } }}
									/>
								</Box>

								<Box
									className="log-evidence-image-wrapper"
									onLoad={(event) => setComponentWidth(event.currentTarget.offsetWidth)}
								>
									<FrameImage
										ref={frameImageRef}
										src={
											selectedTab === LOG_DETAILS_TAB_E.IMAGE
												? eventLogDetails.frame_image
												: selectedTab === LOG_DETAILS_TAB_E.HISTORY
												? latestEventImage.src
												: ""
										}
										bbox={selectedTab === LOG_DETAILS_TAB_E.IMAGE ? eventLogDetails.bbox : []}
										loading={
											selectedTab === LOG_DETAILS_TAB_E.HISTORY
												? getTurnaroundLogsArgs?.page === 1 && (getTurnaroundLogsLoading || getTurnaroundLogsFetching)
												: false
										}
										archived={
											selectedTab === LOG_DETAILS_TAB_E.HISTORY
												? latestEventImage.archived
												: eventLogDetails.bucket_tier === "archive"
										}
										openFullscreen={
											(selectedTab === LOG_DETAILS_TAB_E.IMAGE && eventLogDetails.frame_image) ||
											(selectedTab === LOG_DETAILS_TAB_E.HISTORY && latestEventImage.src)
												? handleShowFullscreenImage
												: undefined
										}
										overlayDetails={{
											auth: convertToTitleCase(eventLogDetails.type),
											clientName: getSocietyName(),
											location: eventLogDetails.camera_name,
											numberPlate: eventLogDetails.vehicle_number,
											productName: societyDetails?.product_name || "GateGuard",
											timestamp: eventLogDetails.timestamp,
											vehicleType: ""
										}}
										width={componentWidth}
										frameID={eventLogDetails.unique_id}
										setShowDownloadButtonLoader={setShowDownloadButtonLoader}
									/>
								</Box>

								<Box
									className={`log-change-arrow ${nextButtonDisabled ? "disabled" : ""}`}
									onClick={() => onNext(eventLogDetails.unique_id)}
									sx={{
										background: "linear-gradient(135deg, rgba(91, 78, 191, 0.1) 0%, rgba(91, 78, 191, 0.05) 100%)",
										border: "1px solid rgba(91, 78, 191, 0.2)",
										borderRadius: "8px",
										transition: "all 0.2s ease-in-out",
										"&:hover": {
											background: "linear-gradient(135deg, rgba(91, 78, 191, 0.15) 0%, rgba(91, 78, 191, 0.08) 100%)",
											transform: "scale(1.05)"
										},
										display: { xs: "none", sm: "flex" }
									}}
								>
									<ChevronRightIcon
										color={nextButtonDisabled ? "disabled" : "primary"}
										sx={{ fontSize: { xs: "32px", sm: "48px" } }}
									/>
								</Box>
							</Box>
						</Grid>

						<Grid item xs={12}>
							<Divider sx={{ margin: { xs: "12px 0", sm: "16px 0" }, borderColor: "rgba(0, 0, 0, 0.08)" }} />
						</Grid>

						{TabRenderer[selectedTab]}
					</Grid>
				) : null}
			</DialogContent>

			{showFullscreenImage ? (
				<Box className="fullscreen-evidence-image-section" onClick={handleHideFullscreenImage}>
					<Box className="fullscreen-evidence-image-wrapper" onClick={(event) => event.stopPropagation()}>
						{eventLogDetails ? (
							<FrameImage
								src={
									selectedTab === LOG_DETAILS_TAB_E.IMAGE
										? eventLogDetails.frame_image
										: selectedTab === LOG_DETAILS_TAB_E.HISTORY
										? latestEventImage.src
										: ""
								}
								bbox={eventLogDetails.bbox}
								overlayDetails={{
									auth: convertToTitleCase(eventLogDetails.type),
									clientName: getSocietyName(),
									location: eventLogDetails.camera_name,
									numberPlate: eventLogDetails.vehicle_number,
									productName: societyDetails?.product_name || "GateGuard",
									timestamp: eventLogDetails.timestamp,
									vehicleType: ""
								}}
								width={componentWidth}
								frameID={eventLogDetails.unique_id}
								setShowDownloadButtonLoader={setShowDownloadButtonLoader}
							/>
						) : null}
					</Box>

					<IconButton
						disableRipple
						sx={{ position: "absolute", top: "0", right: "0" }}
						onClick={handleHideFullscreenImage}
					>
						<FullscreenExitIcon sx={{ color: "white" }} fontSize="large" />
					</IconButton>
				</Box>
			) : null}
		</Dialog>
	);
};

export default LogDetailsDialog;
